import { useState, useEffect } from 'react'
import { ChevronDown } from 'lucide-react'
import { PasswordInput } from '@/components/ui/PasswordInput'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetBody,
  SheetFooter,
} from '@/components/ui/sheet'
import { AlertDialog, AlertDialogContent, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '@/components/ui/alert-dialog'
import { toast } from 'sonner'
import { ApiError } from '@/lib/api-error'
import { createDevice, updateDevice, deleteDevice, Device } from '@/api/devices'
import { cn } from '@/lib/utils'

interface Props {
  open: boolean
  device?: Device | null
  prefillHost?: string
  onClose: () => void
  onSaved: (device: Device) => void
  onDeleted?: (id: string) => void
}

export function DeviceSheet({ open, device, prefillHost, onClose, onSaved, onDeleted }: Props) {
  const isEdit = !!device

  const [name, setName] = useState('')
  const [host, setHost] = useState('')
  const [port, setPort] = useState(80)
  const [username, setUsername] = useState('admin')
  const [password, setPassword] = useState('')
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [credentialsOpen, setCredentialsOpen] = useState(!isEdit)

  useEffect(() => {
    if (device) {
      setName(device.name)
      setHost(device.host)
      setPort(device.port)
      setUsername('')
      setPassword('')
      setCredentialsOpen(false)
    } else {
      setName('')
      setHost(prefillHost ?? '')
      setPort(80)
      setUsername('admin')
      setPassword('')
      setCredentialsOpen(true)
    }
    setErrors({})
  }, [device, prefillHost, open])

  function validate(): boolean {
    const errs: Record<string, string> = {}
    if (!name.trim()) errs.name = 'Nombre es requerido'
    if (!host.trim()) errs.host = 'IP / host es requerido'
    if (port < 1 || port > 65535) errs.port = 'Puerto inválido'
    if (!isEdit) {
      if (!username.trim()) errs.username = 'Usuario es requerido'
      if (!password.trim()) errs.password = 'Contraseña es requerida'
    }
    setErrors(errs)
    // Auto-expand credentials if there are credential errors
    if (errs.username || errs.password) setCredentialsOpen(true)
    return Object.keys(errs).length === 0
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!validate()) return
    setSaving(true)
    try {
      let saved: Device
      if (isEdit) {
        const patch: Parameters<typeof updateDevice>[1] = { name, host, port }
        if (username.trim()) patch.username = username
        if (password.trim()) patch.password = password
        const r = await updateDevice(device.id, patch)
        saved = r.device
        toast.success('Dispositivo actualizado')
      } else {
        const r = await createDevice({ name, host, port, username, password })
        saved = r.device
        toast.success('Dispositivo agregado')
      }
      onSaved(saved)
      onClose()
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Error al guardar')
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete() {
    setDeleting(true)
    try {
      await deleteDevice(device!.id)
      toast.success('Dispositivo eliminado')
      onDeleted!(device!.id)
      onClose()
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Error al eliminar')
    } finally {
      setDeleting(false)
    }
  }

  const inputCls = 'w-full bg-background border border-border rounded-lg px-3 py-2.5 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent transition-colors'
  const labelCls = 'block text-xs font-medium text-text-muted mb-1.5'

  return (
    <>
      <Sheet open={open} onOpenChange={(o) => { if (!o) onClose() }}>
        <SheetContent side="right">
          <SheetHeader>
            <SheetTitle>{isEdit ? 'Editar dispositivo' : 'Nuevo dispositivo'}</SheetTitle>
            {!isEdit && (
              <p className="text-xs text-text-muted">
                Asegurate de que el dispositivo esté encendido y en la misma red.
              </p>
            )}
          </SheetHeader>

          <SheetBody>
            <form id="device-form" onSubmit={handleSubmit} className="flex flex-col gap-5">

              {/* Nombre */}
              <div>
                <label htmlFor="dev-name" className={labelCls}>Nombre</label>
                <input
                  id="dev-name"
                  className={inputCls}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ej: Entrada principal"
                  maxLength={100}
                  disabled={saving}
                />
                {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
              </div>

              {/* Host + Puerto */}
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <label htmlFor="dev-host" className={labelCls}>IP / Host</label>
                  <input
                    id="dev-host"
                    className={inputCls}
                    value={host}
                    onChange={(e) => setHost(e.target.value)}
                    placeholder="192.168.1.64"
                    maxLength={253}
                    disabled={saving}
                  />
                  {errors.host && <p className="text-red-500 text-xs mt-1">{errors.host}</p>}
                </div>
                <div>
                  <label htmlFor="dev-port" className={labelCls}>Puerto</label>
                  <input
                    id="dev-port"
                    className={inputCls}
                    type="number"
                    min={1}
                    max={65535}
                    value={port}
                    onChange={(e) => setPort(Number(e.target.value))}
                    disabled={saving}
                  />
                  {errors.port && <p className="text-red-500 text-xs mt-1">{errors.port}</p>}
                </div>
              </div>

              {/* Credenciales — collapsible */}
              <div className="border border-border rounded-xl overflow-hidden">
                <button
                  type="button"
                  onClick={() => setCredentialsOpen(!credentialsOpen)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-surface hover:bg-border/30 transition-colors text-left"
                  aria-expanded={credentialsOpen}
                >
                  <div>
                    <p className="text-sm font-medium text-text-primary">
                      {isEdit ? 'Cambiar credenciales' : 'Credenciales de acceso'}
                    </p>
                    {isEdit && !credentialsOpen && (
                      <p className="text-xs text-text-muted">Deja en blanco para mantener las actuales</p>
                    )}
                  </div>
                  <ChevronDown
                    className={cn('w-4 h-4 text-text-muted transition-transform duration-200', credentialsOpen && 'rotate-180')}
                  />
                </button>

                {credentialsOpen && (
                  <div className="px-4 pb-4 pt-3 flex flex-col gap-4 border-t border-border">
                    <div>
                      <label htmlFor="dev-user" className={labelCls}>Usuario</label>
                      <input
                        id="dev-user"
                        className={inputCls}
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        placeholder={isEdit ? 'Sin cambios' : 'admin'}
                        maxLength={64}
                        autoComplete="off"
                        disabled={saving}
                      />
                      {errors.username && <p className="text-red-500 text-xs mt-1">{errors.username}</p>}
                    </div>
                    <div>
                      <label htmlFor="dev-pass" className={labelCls}>Contraseña</label>
                      <PasswordInput
                        id="dev-pass"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder={isEdit ? 'Sin cambios' : ''}
                        maxLength={128}
                        autoComplete="new-password"
                        className={inputCls}
                        disabled={saving}
                      />
                      {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
                    </div>
                  </div>
                )}
              </div>

            </form>
          </SheetBody>

          <SheetFooter>
            {isEdit && onDeleted && (
              <button
                type="button"
                disabled={deleting || saving}
                onClick={() => setConfirmDelete(true)}
                className="mr-auto text-sm text-red-500 hover:text-red-400 transition-colors disabled:opacity-50"
              >
                {deleting ? 'Eliminando...' : 'Eliminar'}
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-text-muted hover:text-text-primary transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              form="device-form"
              disabled={saving}
              className="px-5 py-2 bg-accent text-white text-sm font-medium rounded-lg hover:bg-accent/90 disabled:opacity-50 transition-colors"
            >
              {saving ? 'Guardando...' : isEdit ? 'Guardar cambios' : 'Agregar dispositivo'}
            </button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <AlertDialog open={confirmDelete} onOpenChange={() => setConfirmDelete(false)}>
        <AlertDialogContent>
          <AlertDialogTitle>¿Eliminar dispositivo?</AlertDialogTitle>
          <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => { handleDelete(); setConfirmDelete(false) }}>
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
